import { SnackbarType } from 'common/store/actions';
import { has } from 'lodash';
/**
 * Fonction qui détermine si un `s` doit être ajouté à la fin du mot
 * @param count Nombre placé avant le mot
 * @param word Mot à pluraliser
 * @param displayCount Affiche le nombre avant le mot (par défaut: true)
 */
export function pluralize(count, word, displayCount = true) {
    return count > 1
        ? `${displayCount ? count : ''} ${word
            .split(' ')
            .map((wordFragment) => `${wordFragment}s`)
            .join(' ')}`
        : `${displayCount ? count : ''} ${word}`;
}
/**
 * Affiche les deux prénoms d'un membre
 * @param name1 Premier prénom
 * @param name2 Deuxième prénom
 * @return Les deux noms joint d'un `et` ou que le premier s'il n'y a pas de deuxième prénom
 */
export function displayNames(name1, name2) {
    return `${name1} ${name2 ? ` et ${name2}` : ''}`;
}
/**
 * Transforme les âges d'un membre en string
 * @param membre Membre dont on souhaite transformer les âges
 * @return Les âges sous forme de string
 */
export function formatAge(membre) {
    var _a, _b, _c;
    let returnString = (_b = (_a = membre === null || membre === void 0 ? void 0 : membre.infoPublic) === null || _a === void 0 ? void 0 : _a.age1) === null || _b === void 0 ? void 0 : _b.toString();
    if ((_c = membre === null || membre === void 0 ? void 0 : membre.infoPublic) === null || _c === void 0 ? void 0 : _c.age2) {
        returnString += ` et ${membre.infoPublic.age2}`;
    }
    returnString += ' ans';
    return returnString;
}
/**
 * Convertis les albumPhotos ou les sortieImages en fichiers photos
 * @param photos Photos à convertir
 * @return Retourne toutes les photos sauf la première en format fichier photo
 */
export function otherPhotosToB64File(photos) {
    var _a;
    return (_a = photos === null || photos === void 0 ? void 0 : photos.slice(1)) === null || _a === void 0 ? void 0 : _a.map((photo) => photo === null || photo === void 0 ? void 0 : photo.file);
}
/**
 * Vérifie que la valeur rentrée est comprise dans le maximum et qu'il s'agit bien d'un chiffre
 * @param value Valeur à vérifier
 * @param setValue Assigne la nouvelle valeur vérifiée
 * @param error Envoi une notification d'erreur si la valeur n'est pas validée
 * @param max Le maximum (par défaut: 1000000)
 */
export function validateNumber(value, setValue, error, max = 10000) {
    const valueToCheck = parseInt(value, 10);
    if (value.length > 0 && Number.isNaN(valueToCheck)) {
        error('Ce champ ne doit contenir que des nombres', SnackbarType.ERROR);
        return;
    }
    if (value.length === 0 || (valueToCheck > 0 && valueToCheck <= max)) {
        setValue(Number.isNaN(valueToCheck) ? null : valueToCheck);
    }
}
/**
 * Vérifie qu'une valeur est bien un nombre décimal et qu'elle est comprise dans le maximum
 * @param value Valeur à vérifier
 * @param setValue Assigne la nouvelle valeur vérifiée
 * @param error Envoi une notification d'erreur si la valeur n'est pas validée
 * @param max Le maximum (par défaut: 1000000)
 */
export function validateDecimal(value, setValue, error, max = 10000) {
    if ((Number.isNaN(Number(value.slice(-1))) && value.slice(-1) !== ',') || value.slice(0, 2) === '00') {
        error('Ce champ ne doit contenir que des nombres', SnackbarType.ERROR);
        return;
    }
    if (value.length > 0 && !/^[^,]*\d,?[^,]*$/g.test(value)) {
        error("Ce champ ne doit contenir qu'une seule virgule décimale", SnackbarType.ERROR);
        return;
    }
    const [intPart, decimalPart] = value.split(',');
    if ((intPart === null || intPart === void 0 ? void 0 : intPart.length) > max.toString().length || (decimalPart === null || decimalPart === void 0 ? void 0 : decimalPart.length) > 2) {
        error('La valeur rentrée est trop grande', SnackbarType.ERROR);
        return;
    }
    setValue(value);
}
/**
 * Supprime les tags HTML afin d'avoir uniquement le texte
 * @param msg Message dont on souhaite retirer les balises
 * @return Message sans les balises
 */
export function removeTags(message) {
    const regex = /<[^<>]+>/g;
    return message.replaceAll(regex, '').trim();
}
/**
 * Insert du texte à un index donné dans une chaîne de caractères
 * @param originalString Texte d'origine
 * @param index Index où insérer le texte
 * @param textToInsert Texte à insérer
 * @return Le texte d'origine avec le texte inséré à l'index donné
 */
export function insertText(originalString, index, textToInsert) {
    return originalString.slice(0, index) + textToInsert + originalString.slice(index);
}
/**
 * Supprime les valeurs vides d'un objet
 * @param obj Objet dont on souhaite retirer les valeurs vides
 * @return L'objet sans valeur vide
 */
export function removeEmpty(object) {
    return Object.fromEntries(Object.entries(object).filter(([_, value]) => value != null && value !== '' && value !== undefined && !Number.isNaN(value)));
}
/**
 * Utilisé dans le Wysiwyg
 * Vérifie que la chaine de caractère saisie ne dépasse pas une certaine taille.
 * Adapte le code html en fonction de la saisie
 * @param htmlString Le string html
 * @param maxLength La longueur maximale de texte
 * @return Le string html avec la longueur maximale de texte
 */
export function truncateHTMLString(htmlString, maxLength) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    let truncatedText = '';
    let accumulatedLength = 0;
    // Pour chaque noeud HTML
    for (const node of [...doc.body.childNodes]) {
        const nodeText = node.textContent;
        const nodeLength = nodeText ? nodeText.length : 0;
        if (accumulatedLength + nodeLength <= maxLength) {
            truncatedText += node.outerHTML;
            accumulatedLength += nodeLength;
        }
        else {
            const remainingLength = maxLength - accumulatedLength;
            const truncatedNodeText = (nodeText === null || nodeText === void 0 ? void 0 : nodeText.slice(0, remainingLength)) || '';
            truncatedText += truncatedNodeText;
            break;
        }
    }
    return truncatedText;
}
/**
 * Convertis une couleur hexadécimale en RGB
 * @param hex Couleur en hexadécimale
 * @return Couleur en RGB
 */
export function hexToRgb(hex) {
    const result = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
    return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
}
/**
 * Convertis le string passé en paramètre en string utilisable dans une URL
 * @param str string à transformer
 * @return un string utilisable pour une url
 */
export function convertToURL(str) {
    return str
        .normalize('NFD')
        .replaceAll(/[\u0300-\u036F]/g, '')
        .replaceAll(/[^ A-Za-z]/g, '')
        .replaceAll(/ /gi, '-')
        .toLowerCase();
}
/**
 * Cherche de manière récursive dans un objet JSON
 * @param object Objet que l'on veut parcourir
 * @param valueToCompare Valeur que l'on cherche
 * @param callback Fonctions à exécuter lorsqu'un élément est trouvé
 */
export function deepFind(object, valueToCompare, callback) {
    for (const [key, value] of Object.entries(object)) {
        if (typeof value === 'object') {
            if (has(value, valueToCompare)) {
                callback(key);
            }
            else {
                deepFind(value, valueToCompare, callback);
            }
        }
    }
}
//# sourceMappingURL=data:application/json;base64,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